import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { TITLE_MAX } from '@goaltree/backend/convex/lib/coachTitle'
import type { OptimisticLocalStore } from 'convex/browser'

import { patchPrefs } from '@/lib/prefs-optimistic'

type ChatId = Id<'coachThreads'> | null
type ChatRow = { id: ChatId; title: string; updatedAt: number; pinned: boolean }

// Maps (or drops, when fn returns null) the chat row with this id across every cached chat list.
function updateChatRows(
  store: OptimisticLocalStore,
  threadId: ChatId,
  fn: <R extends ChatRow>(row: R) => R | null,
) {
  const apply = <R extends ChatRow>(rows: R[]) =>
    rows.flatMap((r) => (r.id === threadId ? (fn(r) ?? []) : [r]))
  for (const { args, value } of store.getAllQueries(api.coachHistory.listThreads)) {
    if (value)
      store.setQuery(api.coachHistory.listThreads, args, {
        ...value,
        threads: apply(value.threads),
      })
  }
  for (const { args, value } of store.getAllQueries(api.coachHistory.listThreadsPage)) {
    if (value)
      store.setQuery(api.coachHistory.listThreadsPage, args, { ...value, page: apply(value.page) })
  }
  for (const { args, value } of store.getAllQueries(api.coachHistory.searchChats)) {
    if (value) store.setQuery(api.coachHistory.searchChats, args, apply(value))
  }
}

export function optimisticRenameThread(
  store: OptimisticLocalStore,
  { threadId, title }: { threadId: ChatId; title: string },
) {
  const clean = title.trim().replace(/\s+/g, ' ').slice(0, TITLE_MAX)
  if (!clean) return
  updateChatRows(store, threadId, (r) => ({ ...r, title: clean }))
}

export function optimisticSetThreadPinned(
  store: OptimisticLocalStore,
  { threadId, pinned }: { threadId: ChatId; pinned: boolean },
) {
  updateChatRows(store, threadId, (r) => ({ ...r, pinned }))
}

export function optimisticDeleteThread(
  store: OptimisticLocalStore,
  { threadId }: { threadId: ChatId },
) {
  updateChatRows(store, threadId, () => null)
}

export function optimisticSwitchThread(
  store: OptimisticLocalStore,
  { threadId }: { threadId: ChatId },
) {
  for (const { args, value } of store.getAllQueries(api.coachHistory.listThreads)) {
    if (value)
      store.setQuery(api.coachHistory.listThreads, args, { ...value, activeThreadId: threadId })
  }
  patchPrefs(store, { coachThreadId: threadId ?? undefined })
}

type HistoryMsg = NonNullable<ReturnType<typeof getHistoryQueries>[number]['value']>[number]

function getHistoryQueries(store: OptimisticLocalStore) {
  return store.getAllQueries(api.coachHistory.getHistory)
}

function patchMessage(
  store: OptimisticLocalStore,
  messageId: Id<'coachMessages'>,
  fn: (m: HistoryMsg) => HistoryMsg,
) {
  for (const { args, value } of getHistoryQueries(store)) {
    if (!value?.some((m) => m._id === messageId)) continue
    store.setQuery(
      api.coachHistory.getHistory,
      args,
      value.map((m) => (m._id === messageId ? fn(m) : m)),
    )
  }
}

export function optimisticMarkActionUndone(
  store: OptimisticLocalStore,
  { messageId, index }: { messageId: Id<'coachMessages'>; index: number },
) {
  patchMessage(store, messageId, (m) =>
    m.actions?.[index]?.undo
      ? { ...m, actions: m.actions.map((a, i) => (i === index ? { ...a, undone: true } : a)) }
      : m,
  )
}

export function optimisticApproveDeadlines(
  store: OptimisticLocalStore,
  { messageId, taskIds }: { messageId: Id<'coachMessages'>; taskIds?: string[] },
) {
  patchMessage(store, messageId, (m) => {
    if (!m.pendingDeadlines) return m
    const updated = m.pendingDeadlines.map((d) =>
      !d.approved && (!taskIds || taskIds.includes(d.taskId))
        ? { ...d, approved: true as const }
        : d,
    )
    return {
      ...m,
      pendingDeadlines: updated,
      ...(updated.every((d) => d.approved) ? { pendingApproval: false } : {}),
    }
  })
}

export function patchTaskCards(
  store: OptimisticLocalStore,
  taskId: Id<'nodes'>,
  patch: { done?: boolean; inToday?: boolean },
) {
  for (const { args, value } of getHistoryQueries(store)) {
    if (!value?.some((m) => m.taskCards?.some((c) => c._id === taskId))) continue
    store.setQuery(
      api.coachHistory.getHistory,
      args,
      value.map((m) =>
        m.taskCards?.some((c) => c._id === taskId)
          ? { ...m, taskCards: m.taskCards.map((c) => (c._id === taskId ? { ...c, ...patch } : c)) }
          : m,
      ),
    )
  }
}
